import { z } from 'zod'
import type { WorkDetail } from '@/components/ledger/CommonsWork'

const cursor = z.string().nullable()
const decision = z.object({
  id: z.string().optional(),
  outcome: z.string(),
  reason: z.string(),
  createdAt: z.string().optional(),
})
/** Strip unknown fields and require an anonymous, public research response before serialization. */
export const publicWorkDetailSchema: z.ZodType<
  WorkDetail,
  z.ZodTypeDef,
  unknown
> = z.object({
  item: z.object({
    id: z.string().uuid(),
    title: z.string(),
    kind: z.string(),
    status: z.enum(['open', 'closed']),
    brief: z.string(),
    acceptance: z.record(z.string(), z.unknown()),
    baseRef: z
      .object({ repo: z.string().url(), revision: z.string() })
      .nullable(),
    parentId: cursor,
    parentTitle: z.string().optional(),
    parentStatus: z.string().optional(),
    tags: z.array(z.string()).max(8),
  }),
  workspace: z.object({
    slug: z.literal('research'),
    name: z.string(),
    visibility: z.literal('public'),
  }),
  viewer: z.object({
    identityId: z.null(),
    canContribute: z.literal(false),
    canComment: z.literal(false),
    maintainer: z.literal(false),
    claim: z.null(),
  }),
  links: z.object({
    outgoing: z.array(
      z.object({ toType: z.string(), toId: z.string(), relation: z.string(), title: z.string().nullable().optional(), status: z.string().nullable().optional() }),
    ),
    incoming: z.array(
      z.object({ fromItemId: z.string(), relation: z.string(), title: z.string().optional(), status: z.string().optional() }),
    ),
  }),
  activeClaims: z.number(),
  evidence: z
    .array(
      z.object({
        id: z.string(),
        status: z.literal('published'),
        createdAt: z.string().optional(),
        correction: z.object({ corrects: z.string().uuid(), reason: z.string().min(10).max(1000) }).nullable().optional(),
        nextCorrection: z.object({ id: z.string().uuid(), status: z.literal('published') }).nullable().optional(),
        summary: z.string(),
        revision: z.string(),
        command: z.string(),
        outcome: z.string(),
        environment: z.record(z.string(), z.string()),
        result: z.record(z.string(), z.unknown()),
        participant: z.object({ name: z.string(), operator: z.string() }),
        artifacts: z.array(
          z.object({ type: z.string(), url: z.string().url() }),
        ),
        trust: z.object({
          replicated: z.boolean(),
          contested: z.boolean(),
          decision: cursor,
        }),
        replications: z.array(
          z.object({ itemId: z.string(), outcome: z.string() }),
        ),
        nextDecisionCursor: cursor.optional(),
        decisions: z.array(decision).max(5),
      }),
    )
    .max(100),
  nextEvidenceCursor: cursor.optional(),
  progress: z
    .array(
      z.object({
        name: z
          .string()
          .nullable()
          .transform((name) => name ?? 'Unavailable participant'),
        note: z.string(),
        at: z.string(),
      }),
    )
    .max(20),
  comments: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        operator: z.string(),
        note: z.string().default(''),
        replyTo: z
          .string()
          .nullish()
          .transform((value) => value ?? undefined),
        corrects: z.string().uuid().nullish().transform((value) => value ?? undefined),
        correctedBy: z.string().uuid().nullish().transform((value) => value ?? undefined),
        at: z.string(),
        participantId: z.string(),
        withdrawnAt: cursor.optional(),
        moderatedAt: cursor.optional(),
      }),
    )
    .max(100),
  nextCommentCursor: cursor,
})
